// ABOUTME: Provides WhatsApp unread counters and realtime refresh state.
// ABOUTME: Renders the global WhatsApp shortcut shown inside the ATS shell.
import React from "react";
import { navigate } from "@gatsbyjs/reach-router";
import { Badge, Fab, makeStyles, Tooltip } from "@material-ui/core";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import { apiDirectCall } from "customHooks/useApi";
import { getWhatsappApiResponseData } from "functions/whatsappApiResponse";
import { normalizeWhatsappContactEpisodeMode } from "functions/whatsappContactEpisode";
import { useAuthState, useAuthorization } from "_ReactContext/AuthContext";
import {
    getWhatsappUnreadCount,
    normalizeWhatsappUnreadSummary,
    WhatsappUnreadSummary
} from "./whatsappUnread";

interface WhatsappUnreadContextValue extends WhatsappUnreadSummary {
    refresh: () => Promise<void>;
    getUnreadCountForApplicant: (
        applicantId?: string,
        segmentId?: string
    ) => number;
    canShowWhatsappShortcut: boolean;
}

const defaultSummary: WhatsappUnreadSummary = {
    featureEnabled: false,
    connected: false,
    canSendMessages: false,
    totalUnread: 0,
    pendingCount: 0,
    conversations: []
};

const WhatsappUnreadContext = React.createContext<WhatsappUnreadContextValue>({
    ...defaultSummary,
    refresh: async () => {},
    getUnreadCountForApplicant: () => 0,
    canShowWhatsappShortcut: false
});

const buildInboxWebsocketUrl = (
    apiBaseUrl: string,
    params: { ticket: string; companyId: number }
) => {
    const trimmedBaseUrl = apiBaseUrl.replace(/\/$/, "");
    const websocketBaseUrl = trimmedBaseUrl.replace(/^http/i, "ws");
    const searchParams = new URLSearchParams({
        ticket: params.ticket,
        companyId: String(params.companyId)
    });

    return `${websocketBaseUrl}/whatsapp/inboxws?${searchParams.toString()}`;
};

export const WhatsappUnreadProvider = (props: {
    children: React.ReactNode;
}) => {
    const { children } = props;
    const { company, isAuthenticated } = useAuthState();
    const { isRouteAuthorized } = useAuthorization();
    const companyId = Number(company.companyId || 0);
    const routeAuthorized = isRouteAuthorized("whatsappinbox");
    const shortcutEnabled =
        isAuthenticated && company.whatsappEnabled === true && companyId > 0;
    const unreadSummaryEnabled = shortcutEnabled && routeAuthorized;
    const contactEpisodeMode = normalizeWhatsappContactEpisodeMode(
        company.whatsappContactEpisodeMode
    );
    const pendingSummaryEnabled =
        unreadSummaryEnabled &&
        (contactEpisodeMode === "READ_ONLY" || contactEpisodeMode === "ACTIVE");
    const [summary, setSummary] =
        React.useState<WhatsappUnreadSummary>(defaultSummary);
    const refreshRequestRef = React.useRef(0);

    const refresh = React.useCallback(async () => {
        if (!unreadSummaryEnabled) {
            setSummary(defaultSummary);
            return;
        }

        const requestId = refreshRequestRef.current + 1;
        refreshRequestRef.current = requestId;
        const [result, pendingResult] = await Promise.all([
            apiDirectCall("/whatsapp/unreadsummary", "post", {
                companyId,
                contactEpisodeCapability: "CONTACT_EPISODE_V1"
            }),
            pendingSummaryEnabled
                ? apiDirectCall("/whatsapp/unreadsummary", "post", {
                      companyId,
                      scope: "CONTACT_EPISODE"
                  }).catch(() => null)
                : Promise.resolve(null)
        ]);
        if (refreshRequestRef.current !== requestId) {
            return;
        }
        const data = getWhatsappApiResponseData(result);
        const pendingData = pendingResult
            ? getWhatsappApiResponseData(pendingResult)
            : {};
        setSummary({
            ...normalizeWhatsappUnreadSummary(data),
            pendingCount: pendingSummaryEnabled
                ? Number(pendingData.pendingCount || 0)
                : 0
        });
    }, [companyId, pendingSummaryEnabled, unreadSummaryEnabled]);

    React.useEffect(() => {
        void refresh();
        if (!unreadSummaryEnabled) {
            return;
        }

        const interval = window.setInterval(() => {
            void refresh();
        }, 60 * 1000);

        return () => {
            window.clearInterval(interval);
        };
    }, [refresh, unreadSummaryEnabled]);

    React.useEffect(() => {
        if (
            !unreadSummaryEnabled ||
            !summary.featureEnabled ||
            !summary.connected ||
            typeof WebSocket === "undefined"
        ) {
            return;
        }

        let disposed = false;
        let socket: WebSocket | null = null;
        let reconnectTimer: number | null = null;

        const connect = async () => {
            try {
                const ticketResponse = await apiDirectCall(
                    "/whatsapp/inboxticket",
                    "post",
                    { companyId }
                );
                const ticket =
                    getWhatsappApiResponseData(ticketResponse).ticket;
                if (!ticket || disposed) {
                    return;
                }
                socket = new WebSocket(
                    buildInboxWebsocketUrl(import.meta.env.VITE_API_ADDRESS, {
                        ticket,
                        companyId
                    })
                );
                socket.onmessage = () => {
                    void refresh();
                };
                socket.onclose = () => {
                    if (!disposed) {
                        reconnectTimer = window.setTimeout(connect, 3000);
                    }
                };
            } catch {
                if (!disposed) {
                    reconnectTimer = window.setTimeout(connect, 5000);
                }
            }
        };

        connect();

        return () => {
            disposed = true;
            if (reconnectTimer) {
                window.clearTimeout(reconnectTimer);
            }
            socket?.close();
        };
    }, [
        companyId,
        refresh,
        summary.connected,
        summary.featureEnabled,
        unreadSummaryEnabled
    ]);

    const value = React.useMemo<WhatsappUnreadContextValue>(() => {
        const canShowWhatsappShortcut = shortcutEnabled;
        const conversations = unreadSummaryEnabled ? summary.conversations : [];
        const totalUnread = unreadSummaryEnabled ? summary.totalUnread : 0;

        return {
            ...summary,
            totalUnread,
            conversations,
            refresh,
            getUnreadCountForApplicant: (
                applicantId?: string,
                segmentId?: string
            ) => getWhatsappUnreadCount(conversations, applicantId, segmentId),
            canShowWhatsappShortcut
        };
    }, [refresh, shortcutEnabled, summary, unreadSummaryEnabled]);

    return (
        <WhatsappUnreadContext.Provider value={value}>
            {children}
        </WhatsappUnreadContext.Provider>
    );
};

export const useWhatsappUnread = () => React.useContext(WhatsappUnreadContext);

const useFloatingStyles = makeStyles((theme) => ({
    root: {
        position: "fixed",
        right: theme.spacing(3),
        bottom: theme.spacing(10),
        zIndex: theme.zIndex.tooltip
    },
    fab: {
        backgroundColor: "#128c7e",
        color: "#ffffff",
        "&:hover": {
            backgroundColor: "#0f796d"
        }
    }
}));

export const WhatsappFloatingButton = () => {
    const classes = useFloatingStyles();
    const { totalUnread, canShowWhatsappShortcut } = useWhatsappUnread();

    if (!canShowWhatsappShortcut) {
        return null;
    }

    return (
        <Tooltip title="WhatsApp">
            <Badge
                color="secondary"
                badgeContent={totalUnread}
                invisible={totalUnread <= 0}
                className={classes.root}
            >
                <Fab
                    size="medium"
                    className={classes.fab}
                    aria-label="WhatsApp"
                    onClick={() => navigate("/whatsapp")}
                >
                    <WhatsAppIcon />
                </Fab>
            </Badge>
        </Tooltip>
    );
};
